import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  Form,
  FormButton,
  Input,
  number,
  Pre,
  SelectInput,
  string,
  tryParse,
  useNotifications
} from '@ijoin/components';
import {
  useDeleteAdminIntegration,
  useGetAdminIntegration,
  useGetAdminIntegrationTypes,
  usePatchAdminIntegration,
  usePatchAdminIntegrationConfiguration,
  usePatchAdminIntegrationReliusConfiguration,
  usePostAdminTestReliusConnection
} from '@ijoin/hooks';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  AlertTitle,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import { ReliusConsoleTab } from '../../components/ReliusConsoleTab';

type IntegrationForm = {
  description: string;
  integrationTypeId: string;
  rateLimitBatchSize: number;
  rateLimitMaxParallelCalls: number;
  rateLimitTimeout: number;
};

type ConfigForm = {
  connectObject: string;
  delivery: string;
  derProcessingDirectory: string;
  ftp: {
    connTimeout: string;
    directory: string;
    host: string;
    password: string;
    port: string;
    username: string;
  };
  omniConfig: {
    s3ArchiveBucket: string;
    s3Bucket: string;
  };
  recordKeepingOrganizationId: string;
  schedule: string;
};

type ReliusDay = {
  end: string;
  selected: boolean;
  start: string;
};

type ReliusForm = {
  adviceProviderCd: string;
  agentId: string;
  password: string;
  prohibitedDelivery: {
    Friday: ReliusDay;
    Monday: ReliusDay;
    Saturday: ReliusDay;
    Sunday: ReliusDay;
    Thursday: ReliusDay;
    timeZone: string;
    Tuesday: ReliusDay;
    Wednesday: ReliusDay;
  };
  tpaid: string;
  username: string;
  webServiceUrl: string;
};

/* Day keys are Title case to match dayjs.format('dddd') in
   planEnrollments.service.ts's saveToExportQueue lookup. */
const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
] as const;

const RELIUS_TIMEZONES = [
  'EST',
  'EDT',
  'CST',
  'CDT',
  'MST',
  'MDT',
  'PST',
  'PDT',
  'AKST',
  'AKDT',
  'HST'
];

const emptyReliusDay: ReliusDay = {
  end: '17:00',
  selected: false,
  start: '09:00'
};

const emptyReliusForm: ReliusForm = {
  adviceProviderCd: '',
  agentId: '',
  password: '',
  prohibitedDelivery: {
    Friday: emptyReliusDay,
    Monday: emptyReliusDay,
    Saturday: emptyReliusDay,
    Sunday: emptyReliusDay,
    Thursday: emptyReliusDay,
    timeZone: 'EDT',
    Tuesday: emptyReliusDay,
    Wednesday: emptyReliusDay
  },
  tpaid: '',
  username: '',
  webServiceUrl: ''
};

const padTime = (h?: number, m?: number) =>
  `${String(h ?? 0).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;

const parseTime = (value: string) => {
  const [h, m] = value.split(':');
  return { hour: Number(h) || 0, minute: Number(m) || 0 };
};

const extractReliusHeaderValue = (headers: unknown, key: string) => {
  if (!Array.isArray(headers)) return '';
  const entry = headers.find(
    h =>
      !!h &&
      typeof h === 'object' &&
      !Array.isArray(h) &&
      typeof h.element?.[key] === 'string'
  );
  return (entry?.element?.[key] as string | undefined) ?? '';
};

const extractProhibitedDay = (src?: Record<string, unknown>): ReliusDay =>
  src
    ? {
        end: padTime(src.endHour as number, src.endMinute as number),
        selected: !!src.selected,
        start: padTime(src.startHour as number, src.startMinute as number)
      }
    : emptyReliusDay;

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const extractReliusForm = (payload: string): ReliusForm => {
  const parsed = tryParse<Record<string, unknown>>(payload, {}) ?? {};
  const connectObject = toRecord(parsed.connectObject);
  const headers = connectObject.headers;
  const pd = toRecord(parsed.prohibitedDelivery);

  return {
    adviceProviderCd: extractReliusHeaderValue(headers, 'ADVICEPROVIDERCD'),
    agentId: extractReliusHeaderValue(headers, 'AGENTID'),
    password: extractReliusHeaderValue(headers, 'PASSWORD'),
    prohibitedDelivery: {
      Friday: extractProhibitedDay(toRecord(pd.Friday)),
      Monday: extractProhibitedDay(toRecord(pd.Monday)),
      Saturday: extractProhibitedDay(toRecord(pd.Saturday)),
      Sunday: extractProhibitedDay(toRecord(pd.Sunday)),
      Thursday: extractProhibitedDay(toRecord(pd.Thursday)),
      timeZone: typeof pd.timeZone === 'string' ? pd.timeZone : 'EDT',
      Tuesday: extractProhibitedDay(toRecord(pd.Tuesday)),
      Wednesday: extractProhibitedDay(toRecord(pd.Wednesday))
    },
    tpaid: extractReliusHeaderValue(headers, 'TPAID'),
    username: extractReliusHeaderValue(headers, 'USERNAME'),
    webServiceUrl:
      typeof connectObject.webServiceUrl === 'string'
        ? connectObject.webServiceUrl
        : ''
  };
};

const buildProhibitedDay = (day: ReliusDay) => {
  const start = parseTime(day.start);
  const end = parseTime(day.end);

  return {
    endHour: end.hour,
    endMinute: end.minute,
    selected: day.selected,
    startHour: start.hour,
    startMinute: start.minute
  };
};

const buildReliusPayload = (form: ReliusForm) => ({
  adviceProviderCd: form.adviceProviderCd,
  agentId: form.agentId,
  password: form.password,
  prohibitedDelivery: {
    Friday: buildProhibitedDay(form.prohibitedDelivery.Friday),
    Monday: buildProhibitedDay(form.prohibitedDelivery.Monday),
    Saturday: buildProhibitedDay(form.prohibitedDelivery.Saturday),
    Sunday: buildProhibitedDay(form.prohibitedDelivery.Sunday),
    Thursday: buildProhibitedDay(form.prohibitedDelivery.Thursday),
    timeZone: form.prohibitedDelivery.timeZone,
    Tuesday: buildProhibitedDay(form.prohibitedDelivery.Tuesday),
    Wednesday: buildProhibitedDay(form.prohibitedDelivery.Wednesday)
  },
  tpaid: form.tpaid,
  username: form.username,
  webServiceUrl: form.webServiceUrl
});

const integrationResolver = zodResolver(
  z.object({
    description: string({ required: true }),
    integrationTypeId: string({ required: true }),
    rateLimitBatchSize: number(),
    rateLimitMaxParallelCalls: number(),
    rateLimitTimeout: number()
  })
);

const integrationDefaultValues: IntegrationForm = {
  description: '',
  integrationTypeId: '',
  rateLimitBatchSize: 0,
  rateLimitMaxParallelCalls: 0,
  rateLimitTimeout: 0
};

const Page = () => {
  const params = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const integration = useGetAdminIntegration(params.integrationId);
  const integrationTypes = useGetAdminIntegrationTypes();

  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [tab, setTab] = useState('settings');
  const [configJsonError, setConfigJsonError] = useState('');
  const [reliusForm, setReliusForm] = useState<ReliusForm>(emptyReliusForm);
  const [configForm, setConfigForm] = useState<ConfigForm>({
    connectObject: '{}',
    delivery: '',
    derProcessingDirectory: '',
    ftp: {
      connTimeout: '',
      directory: '',
      host: '',
      password: '',
      port: '',
      username: ''
    },
    omniConfig: {
      s3ArchiveBucket: '',
      s3Bucket: ''
    },
    recordKeepingOrganizationId: '',
    schedule: ''
  });

  const integrationTypeName = (integrationTypes.data ?? []).find(
    t => t.integrationTypeId === integration.data?.integrationTypeId
  )?.typeName;
  const isRelius = integrationTypeName === 'ReliusConnect';
  const isSuperAdmin = localStorage.getItem('isAdmin') === '1';

  const integrationMethods = useForm<IntegrationForm>({
    defaultValues: integrationDefaultValues,
    mode: 'all',
    resolver: integrationResolver
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [`/api/v3/admin/integrations/${params.integrationId}`]
    });
  };

  const patchIntegration = usePatchAdminIntegration();
  const patchConfiguration = usePatchAdminIntegrationConfiguration();
  const patchReliusConfiguration =
    usePatchAdminIntegrationReliusConfiguration();
  const testReliusConnection = usePostAdminTestReliusConnection();
  const deleteIntegration = useDeleteAdminIntegration({
    mutation: {
      onError: () => notifications.error('Failed to delete integration'),
      onSuccess: () => {
        notifications.success('Integration deleted');
        navigate({ to: '/integrations' });
      }
    }
  });

  useEffect(() => {
    if (!integration.data) return;

    integrationMethods.reset({
      description: integration.data.description ?? '',
      integrationTypeId: integration.data.integrationTypeId ?? '',
      rateLimitBatchSize: integration.data.rateLimitBatchSize ?? 0,
      rateLimitMaxParallelCalls:
        integration.data.rateLimitMaxParallelCalls ?? 0,
      rateLimitTimeout: integration.data.rateLimitTimeout ?? 0
    });

    const parsed =
      tryParse<Record<string, any>>(
        integration.data.configurationPayload ?? '',
        {}
      ) ?? {};

    const ftp = parsed.ftp ?? {};
    const omni = parsed.omniConfig ?? {};

    setConfigForm({
      connectObject: parsed.connectObject
        ? JSON.stringify(parsed.connectObject, null, 2)
        : '{}',
      delivery: parsed.delivery ?? '',
      derProcessingDirectory: parsed.derProcessingDirectory ?? '',
      ftp: {
        connTimeout: String(ftp.connTimeout ?? ''),
        directory: ftp.directory ?? '',
        host: ftp.host ?? '',
        password: ftp.password ?? '',
        port: String(ftp.port ?? ''),
        username: ftp.username ?? ''
      },
      omniConfig: {
        s3ArchiveBucket: omni.s3ArchiveBucket ?? '',
        s3Bucket: omni.s3Bucket ?? ''
      },
      recordKeepingOrganizationId: parsed.recordKeepingOrganizationId ?? '',
      schedule: parsed.schedule ?? ''
    });

    setReliusForm(
      extractReliusForm(integration.data.configurationPayload ?? '{}')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.data]);

  const onSave = async (integrationForm: IntegrationForm) => {
    if (isRelius) {
      try {
        await Promise.all([
          patchIntegration.mutateAsync({
            data: integrationForm,
            integrationId: params.integrationId
          }),
          patchReliusConfiguration.mutateAsync({
            data: buildReliusPayload(reliusForm),
            integrationId: params.integrationId
          })
        ]);
        invalidate();
        notifications.success('Integration updated');
      } catch {
        notifications.error('Failed to update integration');
      }
      return;
    }

    let connectObject = {};

    try {
      connectObject = JSON.parse(configForm.connectObject);
    } catch {
      setConfigJsonError('Invalid JSON in Connect Object');
      return;
    }

    const configuration = {
      ...(configForm.connectObject !== '{}' ? { connectObject } : {}),
      ...(configForm.delivery ? { delivery: configForm.delivery } : {}),
      ...(configForm.derProcessingDirectory
        ? { derProcessingDirectory: configForm.derProcessingDirectory }
        : {}),
      ...(Object.values(configForm.ftp).some(v => v !== '')
        ? { ftp: configForm.ftp }
        : {}),
      ...(Object.values(configForm.omniConfig).some(v => v !== '')
        ? { omniConfig: configForm.omniConfig }
        : {}),
      ...(configForm.recordKeepingOrganizationId
        ? {
            recordKeepingOrganizationId: configForm.recordKeepingOrganizationId
          }
        : {}),
      ...(configForm.schedule ? { schedule: configForm.schedule } : {})
    };

    try {
      await Promise.all([
        patchIntegration.mutateAsync({
          data: integrationForm,
          integrationId: params.integrationId
        }),
        patchConfiguration.mutateAsync({
          data: { configuration },
          integrationId: params.integrationId
        })
      ]);
      invalidate();
      notifications.success('Integration updated');
    } catch {
      notifications.error('Failed to update integration');
    }
  };

  const onTabChange = (_event: SyntheticEvent, value: string) => setTab(value);

  const onConfirmDelete = () => {
    toggleDeleteOpen();
    deleteIntegration.mutate({ integrationId: params.integrationId });
  };

  const integrationTypeOptions = (integrationTypes.data ?? []).map(t => ({
    label: t.description || t.typeName,
    value: t.integrationTypeId
  }));

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link to='/integrations'>Integrations</Link>
        <Typography color='text.primary'>
          {integration.data?.description ?? '...'}
        </Typography>
      </Breadcrumbs>

      {integration.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack
              alignItems='flex-start'
              direction='row'
              justifyContent='space-between'>
              <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Description
                  </Typography>
                  <Typography variant='h5'>
                    {integration.data.description}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Integration Type
                  </Typography>
                  <Typography variant='subtitle1'>
                    {integration.data.IntegrationTypes?.description ??
                      integration.data.integrationTypeId ??
                      '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Integration ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {params.integrationId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={params.integrationId} />
                  </Stack>
                </div>
                {isRelius && (
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Status
                    </Typography>
                    <Stack alignItems='center' direction='row' spacing={0.75}>
                      {testReliusConnection.isPending ? (
                        <CircularProgress size={18} />
                      ) : testReliusConnection.data?.success ? (
                        <CheckCircleIcon
                          color='success'
                          data-testid='reliusStatusSuccess'
                        />
                      ) : testReliusConnection.data ? (
                        <CancelIcon
                          color='error'
                          data-testid='reliusStatusError'
                        />
                      ) : (
                        <Typography color='text.secondary' variant='body2'>
                          Not tested
                        </Typography>
                      )}
                      {testReliusConnection.data?.success &&
                        testReliusConnection.data.version && (
                          <Typography color='text.secondary' variant='body2'>
                            {testReliusConnection.data.version}
                          </Typography>
                        )}
                    </Stack>
                  </div>
                )}
              </Stack>
              <Stack alignItems='center' direction='row' spacing={1}>
                {isRelius && (
                  <Button
                    data-testid='testReliusConnection'
                    disabled={testReliusConnection.isPending}
                    onClick={() =>
                      testReliusConnection.mutate({
                        integrationId: params.integrationId
                      })
                    }
                    variant='outlined'>
                    Test Connection
                  </Button>
                )}
                <ActionMenu
                  actions={[{ content: 'Delete', onSelect: toggleDeleteOpen }]}
                  trigger={
                    <IconButton data-testid='actionsIntegration'>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                <ConfirmationDialog
                  confirmationText='DELETE'
                  confirmColor='error'
                  confirmText='Delete'
                  data-testid='deleteIntegration'
                  isOpen={isDeleteOpen}
                  message={`Are you sure you want to delete "${integration.data.description}"? This action cannot be undone.`}
                  onCancel={toggleDeleteOpen}
                  onConfirm={onConfirmDelete}
                  title='Delete Integration'
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {isRelius && isSuperAdmin && (
        <Tabs onChange={onTabChange} value={tab}>
          <Tab data-testid='settingsTab' label='Settings' value='settings' />
          <Tab
            data-testid='reliusConsoleTab'
            label='Relius Console'
            value='console'
          />
        </Tabs>
      )}

      {tab === 'console' && (
        <ReliusConsoleTab integrationId={params.integrationId} />
      )}

      {tab === 'settings' && (
        <>
          {isRelius &&
            testReliusConnection.data &&
            !testReliusConnection.data.success && (
              <Alert data-testid='reliusStatusErrorDetails' severity='error'>
                <AlertTitle>Test connection failed</AlertTitle>
                <Pre sx={{ maxHeight: 240, p: 0 }}>
                  {JSON.stringify(testReliusConnection.data.error, null, 2)}
                </Pre>
              </Alert>
            )}

          {isRelius && !!testReliusConnection.data?.planvoice?.length && (
            <Card>
              <CardContent>
                <Typography sx={{ mb: 1 }} variant='subtitle1'>
                  PLANVOICE Status
                </Typography>

                <Grid container sx={{ alignItems: 'center' }}>
                  <Grid size={5}>
                    <Typography color='text.secondary' variant='caption'>
                      Plan / Org
                    </Typography>
                  </Grid>
                  <Grid size={2}>
                    <Typography color='text.secondary' variant='caption'>
                      ENABLEIJOINCD
                    </Typography>
                  </Grid>
                  <Grid size={2}>
                    <Typography color='text.secondary' variant='caption'>
                      ALLOWIJOINENROLLCD
                    </Typography>
                  </Grid>
                  <Grid size={3}>
                    <Typography color='text.secondary' variant='caption'>
                      STATCD
                    </Typography>
                  </Grid>

                  {testReliusConnection.data.planvoice.map(p => (
                    <Fragment key={p.planId}>
                      <Grid size={5} sx={{ py: 0.5 }}>
                        <Typography noWrap variant='body2'>
                          {p.planName ?? p.externalId}
                        </Typography>
                        <Typography
                          color='text.secondary'
                          noWrap
                          variant='caption'>
                          {p.advisoryOrganizationName ?? p.externalId}
                        </Typography>
                      </Grid>

                      <Grid size={2} sx={{ py: 0.5 }}>
                        {p.enableIjoinCd === 'Y' ? (
                          <CheckCircleIcon color='success' />
                        ) : (
                          <Typography color='error' variant='body2'>
                            {p.enableIjoinCd ?? '–'}
                          </Typography>
                        )}
                      </Grid>

                      <Grid size={2} sx={{ py: 0.5 }}>
                        <Typography color='text.secondary' variant='body2'>
                          {p.allowIjoinEnrollCd ?? '–'}
                        </Typography>
                      </Grid>

                      <Grid size={3} sx={{ py: 0.5 }}>
                        {p.statCd === 'W' || p.statCd === 'B' ? (
                          <CheckCircleIcon color='success' />
                        ) : (
                          <Typography color='error' variant='body2'>
                            {p.statCd ?? '–'}
                          </Typography>
                        )}
                      </Grid>
                    </Fragment>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          <Form
            methods={integrationMethods}
            noValidate
            onSubmit={integrationMethods.handleSubmit(onSave)}>
            <Stack spacing={2}>
              <Typography variant='subtitle1'>Settings</Typography>

              <Grid container spacing={2}>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <Input
                    data-testid='editDescription'
                    format='string'
                    fullWidth
                    hideError
                    label='Description'
                    name='description'
                    required
                    size='small'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <SelectInput
                    data-testid='editIntegrationTypeId'
                    fullWidth
                    hideError
                    label='Integration Type'
                    name='integrationTypeId'
                    options={integrationTypeOptions}
                    placeholder='Select a type'
                    required
                    size='small'
                    variant='outlined'
                  />
                </Grid>
                <Grid size={{ sm: 4, xs: 12 }}>
                  <Input
                    data-testid='editRateLimitTimeout'
                    format='number'
                    fullWidth
                    hideError
                    label='Rate Limit Timeout'
                    name='rateLimitTimeout'
                    size='small'
                  />
                </Grid>
                <Grid size={{ sm: 4, xs: 12 }}>
                  <Input
                    data-testid='editRateLimitMaxParallelCalls'
                    format='number'
                    fullWidth
                    hideError
                    label='Rate Limit Max Parallel Calls'
                    name='rateLimitMaxParallelCalls'
                    size='small'
                  />
                </Grid>
                <Grid size={{ sm: 4, xs: 12 }}>
                  <Input
                    data-testid='editRateLimitBatchSize'
                    format='number'
                    fullWidth
                    hideError
                    label='Rate Limit Batch Size'
                    name='rateLimitBatchSize'
                    size='small'
                  />
                </Grid>
              </Grid>

              {!isRelius && (
                <>
                  <Typography variant='subtitle1'>Configuration</Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='configSchedule'
                        fullWidth
                        label='Schedule'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            schedule: e.target.value
                          }))
                        }
                        size='small'
                        value={configForm.schedule}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='configDelivery'
                        fullWidth
                        label='Delivery'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            delivery: e.target.value
                          }))
                        }
                        size='small'
                        value={configForm.delivery}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='configDerProcessingDirectory'
                        fullWidth
                        label='DER Processing Directory'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            derProcessingDirectory: e.target.value
                          }))
                        }
                        size='small'
                        value={configForm.derProcessingDirectory}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant='subtitle1'>FTP Settings</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpHost'
                        fullWidth
                        label='Host'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, host: e.target.value }
                          }))
                        }
                        size='small'
                        value={configForm.ftp.host}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpPort'
                        fullWidth
                        label='Port'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, port: e.target.value }
                          }))
                        }
                        size='small'
                        value={configForm.ftp.port}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpUsername'
                        fullWidth
                        label='Username'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, username: e.target.value }
                          }))
                        }
                        size='small'
                        value={configForm.ftp.username}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpPassword'
                        fullWidth
                        label='Password'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, password: e.target.value }
                          }))
                        }
                        size='small'
                        type='password'
                        value={configForm.ftp.password}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpDirectory'
                        fullWidth
                        label='Directory'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, directory: e.target.value }
                          }))
                        }
                        size='small'
                        value={configForm.ftp.directory}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='ftpConnTimeout'
                        fullWidth
                        label='Connection Timeout'
                        onChange={e =>
                          setConfigForm(prev => ({
                            ...prev,
                            ftp: { ...prev.ftp, connTimeout: e.target.value }
                          }))
                        }
                        size='small'
                        value={configForm.ftp.connTimeout}
                      />
                    </Grid>
                  </Grid>

                  {integrationTypeName === 'OMNI' && (
                    <>
                      <Typography variant='subtitle1'>Omni Config</Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ sm: 6, xs: 12 }}>
                          <TextField
                            data-testid='omniS3Bucket'
                            fullWidth
                            label='S3 Bucket'
                            onChange={e =>
                              setConfigForm(prev => ({
                                ...prev,
                                omniConfig: {
                                  ...prev.omniConfig,
                                  s3Bucket: e.target.value
                                }
                              }))
                            }
                            size='small'
                            value={configForm.omniConfig.s3Bucket}
                          />
                        </Grid>
                        <Grid size={{ sm: 6, xs: 12 }}>
                          <TextField
                            data-testid='omniS3ArchiveBucket'
                            fullWidth
                            label='S3 Archive Bucket'
                            onChange={e =>
                              setConfigForm(prev => ({
                                ...prev,
                                omniConfig: {
                                  ...prev.omniConfig,
                                  s3ArchiveBucket: e.target.value
                                }
                              }))
                            }
                            size='small'
                            value={configForm.omniConfig.s3ArchiveBucket}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <Typography variant='subtitle1'>Connect Object</Typography>
                  <TextField
                    data-testid='connectObjectJson'
                    error={configJsonError.includes('Connect Object')}
                    fullWidth
                    helperText={
                      configJsonError.includes('Connect Object')
                        ? configJsonError
                        : ''
                    }
                    label='Connect Object JSON'
                    multiline
                    onChange={e => {
                      const val = e.target.value;
                      setConfigForm(prev => ({
                        ...prev,
                        connectObject: val
                      }));
                      try {
                        JSON.parse(val);
                        setConfigJsonError('');
                      } catch {
                        setConfigJsonError('Invalid JSON in Connect Object');
                      }
                    }}
                    rows={8}
                    size='small'
                    value={configForm.connectObject}
                  />
                </>
              )}

              {isRelius && (
                <>
                  <Typography variant='subtitle1'>
                    Relius Connect Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        data-testid='reliusWebServiceUrl'
                        fullWidth
                        label='Web Service URL'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            webServiceUrl: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.webServiceUrl}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='reliusUsername'
                        fullWidth
                        label='Username'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            username: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.username}
                      />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='reliusPassword'
                        fullWidth
                        label='Password'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            password: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.password}
                      />
                    </Grid>
                    <Grid size={{ sm: 4, xs: 12 }}>
                      <TextField
                        data-testid='reliusTpaid'
                        fullWidth
                        label='TPA ID'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            tpaid: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.tpaid}
                      />
                    </Grid>
                    <Grid size={{ sm: 4, xs: 12 }}>
                      <TextField
                        data-testid='reliusAgentId'
                        fullWidth
                        label='Agent ID'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            agentId: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.agentId}
                      />
                    </Grid>
                    <Grid size={{ sm: 4, xs: 12 }}>
                      <TextField
                        data-testid='reliusAdviceProviderCd'
                        fullWidth
                        label='Advice Provider Code'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            adviceProviderCd: e.target.value
                          }))
                        }
                        size='small'
                        value={reliusForm.adviceProviderCd}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant='subtitle1'>
                    Prohibited Delivery
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ sm: 6, xs: 12 }}>
                      <TextField
                        data-testid='reliusTimeZone'
                        fullWidth
                        label='Time Zone'
                        onChange={e =>
                          setReliusForm(prev => ({
                            ...prev,
                            prohibitedDelivery: {
                              ...prev.prohibitedDelivery,
                              timeZone: e.target.value
                            }
                          }))
                        }
                        select
                        size='small'
                        value={reliusForm.prohibitedDelivery.timeZone}>
                        {RELIUS_TIMEZONES.map(tz => (
                          <MenuItem key={tz} value={tz}>
                            {tz}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Stack spacing={1}>
                    {WEEKDAYS.map(day => (
                      <Grid alignItems='center' container key={day} spacing={2}>
                        <Grid size={{ sm: 3, xs: 12 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  reliusForm.prohibitedDelivery[day].selected
                                }
                                data-testid={`reliusDay-${day}`}
                                onChange={e =>
                                  setReliusForm(prev => ({
                                    ...prev,
                                    prohibitedDelivery: {
                                      ...prev.prohibitedDelivery,
                                      [day]: {
                                        ...prev.prohibitedDelivery[day],
                                        selected: e.target.checked
                                      }
                                    }
                                  }))
                                }
                              />
                            }
                            label={day[0].toUpperCase() + day.slice(1)}
                          />
                        </Grid>
                        <Grid size={{ sm: 4, xs: 6 }}>
                          <TextField
                            data-testid={`reliusDay-${day}-start`}
                            disabled={
                              !reliusForm.prohibitedDelivery[day].selected
                            }
                            fullWidth
                            label='Start'
                            onChange={e =>
                              setReliusForm(prev => ({
                                ...prev,
                                prohibitedDelivery: {
                                  ...prev.prohibitedDelivery,
                                  [day]: {
                                    ...prev.prohibitedDelivery[day],
                                    start: e.target.value
                                  }
                                }
                              }))
                            }
                            size='small'
                            type='time'
                            value={reliusForm.prohibitedDelivery[day].start}
                          />
                        </Grid>
                        <Grid size={{ sm: 4, xs: 6 }}>
                          <TextField
                            data-testid={`reliusDay-${day}-end`}
                            disabled={
                              !reliusForm.prohibitedDelivery[day].selected
                            }
                            fullWidth
                            label='End'
                            onChange={e =>
                              setReliusForm(prev => ({
                                ...prev,
                                prohibitedDelivery: {
                                  ...prev.prohibitedDelivery,
                                  [day]: {
                                    ...prev.prohibitedDelivery[day],
                                    end: e.target.value
                                  }
                                }
                              }))
                            }
                            size='small'
                            type='time'
                            value={reliusForm.prohibitedDelivery[day].end}
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </Stack>
                </>
              )}

              <Stack direction='row' justifyContent='flex-end'>
                <FormButton
                  data-testid='save'
                  disabled={
                    patchIntegration.isPending ||
                    patchConfiguration.isPending ||
                    patchReliusConfiguration.isPending ||
                    (!isRelius && !!configJsonError)
                  }
                  triggerValidation
                  variant='contained'>
                  Save
                </FormButton>
              </Stack>
            </Stack>
          </Form>
        </>
      )}
    </Stack>
  );
};

export const Route = createFileRoute('/integrations/$integrationId')({
  component: Page
});
